import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, CheckCircle2, ChevronLeft, Loader2, Plus, Rocket, Thermometer, Trash2 } from 'lucide-react';
import {
  newKey, STORE_LAYOUT_LABELS, STORE_LAYOUTS, templateItemSchema, validateTemplateContent,
  type StoreLayout, type TemplateContent, type TemplateItem, type TemplateStatus, type TemplateVisibility,
} from '@bp/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Store, TemplateDetail } from '@/lib/types';
import { cn, fmtDateTime } from '@/lib/utils';
import { Badge, Button, Card, CardHeader, Dialog, ErrorNote, Field, Input, Loading, Select, Tabs, Textarea } from '@/components/ui';

type Meta = {
  name: string; description: string; audit_type: string; operational_purpose: string; status: TemplateStatus;
  visibility: TemplateVisibility; layouts: StoreLayout[]; store_ids: string[]; is_default: boolean;
};
type Tab = 'checklist' | 'scoring' | 'labor' | 'availability' | 'history';

function move<T>(arr: T[], i: number, d: -1 | 1): T[] {
  const j = i + d;
  if (j < 0 || j >= arr.length) return arr;
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

export function TemplateEditorPage() {
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: ['template', id], queryFn: () => api.get<TemplateDetail>(`/api/templates/${id}`), refetchOnWindowFocus: false });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  return <Editor key={id} t={q.data!} refetch={q.refetch} />;
}

function Editor({ t, refetch }: { t: TemplateDetail; refetch: () => Promise<unknown> }) {
  const { can } = useAuth();
  const readOnly = !can('manage_templates');
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('checklist');
  const [content, setContent] = useState<TemplateContent>(t.draft);
  const [meta, setMeta] = useState<Meta>({
    name: t.name, description: t.description, audit_type: t.audit_type, operational_purpose: t.operational_purpose, status: t.status,
    visibility: t.visibility, layouts: t.layouts, store_ids: t.store_ids, is_default: t.is_default,
  });
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [publishOpen, setPublishOpen] = useState(false);
  // Unsaved edits wait here until the debounce fires; refs so no edit is lost to a stale closure.
  const pendingMeta = useRef<Partial<Meta>>({});
  const pendingContent = useRef<TemplateContent | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const errors = useMemo(() => validateTemplateContent(content), [content]);
  const stores = useQuery({ queryKey: ['stores', 'all'], queryFn: () => api.get<Store[]>('/api/stores'), enabled: tab === 'availability' });

  const flushSave = async () => {
    window.clearTimeout(timer.current);
    const body: Record<string, unknown> = { ...pendingMeta.current };
    if (pendingContent.current) body.content = pendingContent.current;
    pendingMeta.current = {};
    pendingContent.current = null;
    if (Object.keys(body).length === 0) return;
    setSaving('saving');
    try {
      await api.patch(`/api/templates/${t.id}`, body);
      setSaving('saved');
      void qc.invalidateQueries({ queryKey: ['templates'] });
    } catch (e) {
      setSaving('error');
      toast.error((e as Error).message);
      throw e;
    }
  };
  const schedule = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void flushSave().catch(() => undefined), 900);
  };
  const updateContent = (fn: (c: TemplateContent) => TemplateContent) => {
    if (readOnly) return;
    setContent((c) => {
      const next = fn(c);
      pendingContent.current = next;
      return next;
    });
    schedule();
  };
  const updateMeta = (patch: Partial<Meta>) => {
    if (readOnly) return;
    setMeta((m) => ({ ...m, ...patch }));
    pendingMeta.current = { ...pendingMeta.current, ...patch };
    schedule();
  };
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const setItem = (si: number, ii: number, patch: Partial<TemplateItem>) =>
    updateContent((c) => ({ ...c, sections: c.sections.map((s, i) => (i !== si ? s : { ...s, items: s.items.map((it, j) => (j !== ii ? it : { ...it, ...patch })) })) }));

  const itemCount = content.sections.reduce((a, s) => a + s.items.length, 0);

  return (
    <>
      <Link to="/templates" className="mb-2 inline-flex items-center gap-1 text-sm text-muted-fg hover:text-fg"><ChevronLeft className="size-4" />Templates</Link>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <input
            value={meta.name}
            disabled={readOnly}
            onChange={(e) => updateMeta({ name: e.target.value })}
            className="w-full max-w-xl rounded-md bg-transparent px-1 text-xl font-semibold hover:bg-muted focus:bg-card sm:text-2xl"
          />
          <div className="mt-1 flex flex-wrap items-center gap-2 px-1 text-sm text-muted-fg">
            <span>{content.sections.length} sections · {itemCount} items</span>
            <span>· {t.current_version_no ? `published v${t.current_version_no}` : 'never published'}</span>
            {saving === 'saving' && <span className="inline-flex items-center gap-1"><Loader2 className="size-3.5 animate-spin" />Saving draft…</span>}
            {saving === 'saved' && <span className="inline-flex items-center gap-1 text-pass"><CheckCircle2 className="size-3.5" />Draft saved</span>}
          </div>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <Select className="w-36" value={meta.status} onChange={(e) => updateMeta({ status: e.target.value as TemplateStatus })}>
              {meta.status === 'draft' && <option value="draft">Draft</option>}
              <option value="active" disabled={!t.current_version_id}>Active</option>
              <option value="inactive" disabled={!t.current_version_id}>Inactive</option>
              <option value="archived" disabled={!t.current_version_id}>Archived</option>
            </Select>
            <Button onClick={() => setPublishOpen(true)} disabled={errors.length > 0}><Rocket className="size-4" />Publish</Button>
          </div>
        )}
      </div>
      {errors.length > 0 && !readOnly && <div className="mb-4 rounded-lg bg-warn-bg px-4 py-2 text-sm text-warn">Fix before publishing: {errors[0]}{errors.length > 1 && ` (+${errors.length - 1} more)`}</div>}

      <Tabs<Tab> className="mb-4 w-fit" value={tab} onChange={setTab} tabs={[
        { value: 'checklist', label: 'Checklist' }, { value: 'scoring', label: 'Scoring' }, { value: 'labor', label: 'Labor' },
        { value: 'availability', label: 'Where it’s used' }, { value: 'history', label: `Versions (${t.versions.length})` },
      ]} />

      {tab === 'checklist' && (
        <div className="space-y-4">
          {content.sections.map((s, si) => (
            <Card key={s.key}>
              <div className="flex items-center gap-2 border-b px-4 py-3">
                <Input value={s.label} disabled={readOnly} className="max-w-md font-semibold" onChange={(e) => updateContent((c) => ({ ...c, sections: c.sections.map((x, i) => (i === si ? { ...x, label: e.target.value } : x)) }))} />
                <span className="text-sm text-muted-fg">{s.items.length} items</span>
                {!readOnly && (
                  <div className="ml-auto flex">
                    <Button size="sm" variant="ghost" onClick={() => updateContent((c) => ({ ...c, sections: move(c.sections, si, -1) }))} aria-label="Move section up"><ArrowUp className="size-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => updateContent((c) => ({ ...c, sections: move(c.sections, si, 1) }))} aria-label="Move section down"><ArrowDown className="size-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-fail" onClick={() => confirm(`Delete section "${s.label}" and its ${s.items.length} items?`) && updateContent((c) => ({ ...c, sections: c.sections.filter((_, i) => i !== si) }))} aria-label="Delete section"><Trash2 className="size-4" /></Button>
                  </div>
                )}
              </div>
              <ul className="divide-y">
                {s.items.map((it, ii) => (
                  <li key={it.key} className="space-y-2 px-4 py-3">
                    <div className="flex items-start gap-2">
                      <Textarea value={it.text} disabled={readOnly} className="min-h-10 flex-1" rows={1} onChange={(e) => setItem(si, ii, { text: e.target.value })} />
                      {!readOnly && (
                        <div className="flex flex-none">
                          <Button size="sm" variant="ghost" onClick={() => updateContent((c) => ({ ...c, sections: c.sections.map((x, i) => (i === si ? { ...x, items: move(x.items, ii, -1) } : x)) }))} aria-label="Move up"><ArrowUp className="size-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => updateContent((c) => ({ ...c, sections: c.sections.map((x, i) => (i === si ? { ...x, items: move(x.items, ii, 1) } : x)) }))} aria-label="Move down"><ArrowDown className="size-4" /></Button>
                          <Button size="sm" variant="ghost" className="text-fail" onClick={() => updateContent((c) => ({ ...c, sections: c.sections.map((x, i) => (i === si ? { ...x, items: x.items.filter((_, j) => j !== ii) } : x)) }))} aria-label="Delete item"><Trash2 className="size-4" /></Button>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                      <Select className="h-8 w-40" disabled={readOnly} value={it.input_type} onChange={(e) => setItem(si, ii, e.target.value === 'temperature' ? { input_type: 'temperature', allow_na: false } : { input_type: 'pass_fail', temp_min: null, temp_max: null })}>
                        <option value="pass_fail">Pass / Fail</option>
                        <option value="temperature">Temperature</option>
                      </Select>
                      {it.input_type === 'temperature' && (
                        <span className="inline-flex items-center gap-1.5">
                          <Thermometer className="size-4 text-info" />
                          <Input className="h-8 w-20" type="number" placeholder="min" disabled={readOnly} value={it.temp_min ?? ''} onChange={(e) => setItem(si, ii, { temp_min: e.target.value === '' ? null : Number(e.target.value) })} />
                          to
                          <Input className="h-8 w-20" type="number" placeholder="max" disabled={readOnly} value={it.temp_max ?? ''} onChange={(e) => setItem(si, ii, { temp_max: e.target.value === '' ? null : Number(e.target.value) })} />
                          <Select className="h-8 w-16" disabled={readOnly} value={it.temp_unit} onChange={(e) => setItem(si, ii, { temp_unit: e.target.value as 'F' | 'C' })}><option value="F">°F</option><option value="C">°C</option></Select>
                        </span>
                      )}
                      <label className="inline-flex items-center gap-1.5">Points <Input className="h-8 w-16" type="number" min={0} disabled={readOnly} value={it.points} onChange={(e) => setItem(si, ii, { points: Math.max(0, Number(e.target.value) || 0) })} /></label>
                      {(['allow_na', 'allow_comment', 'allow_photo', 'allow_video', 'is_critical'] as const).map((k) => (
                        <label key={k} className={cn('inline-flex items-center gap-1.5', k === 'allow_na' && it.input_type === 'temperature' && 'opacity-50')}>
                          <input type="checkbox" className="size-4" disabled={readOnly} checked={it[k]} onChange={(e) => setItem(si, ii, { [k]: e.target.checked })} />
                          {{ allow_na: 'N/A', allow_comment: 'Note', allow_photo: 'Photo', allow_video: 'Video', is_critical: 'Critical' }[k]}
                        </label>
                      ))}
                    </div>
                    <Input className="h-8 text-xs" placeholder="SOP link (https://…)" disabled={readOnly} value={it.sop_url ?? ''} onChange={(e) => setItem(si, ii, { sop_url: e.target.value.trim() || null })} />
                  </li>
                ))}
              </ul>
              {!readOnly && (
                <div className="border-t px-4 py-2">
                  <Button size="sm" variant="ghost" onClick={() => updateContent((c) => ({ ...c, sections: c.sections.map((x, i) => (i === si ? { ...x, items: [...x.items, templateItemSchema.parse({ key: newKey('item'), text: 'New check' })] } : x)) }))}><Plus className="size-4" />Add item</Button>
                </div>
              )}
            </Card>
          ))}
          {!readOnly && <Button variant="outline" onClick={() => updateContent((c) => ({ ...c, sections: [...c.sections, { key: newKey('sec'), label: 'New section', items: [] }] }))}><Plus className="size-4" />Add section</Button>}
        </div>
      )}

      {tab === 'scoring' && (
        <Card className="max-w-xl">
          <CardHeader title="Scoring rules" subtitle="N/A and unanswered items never count against the score." />
          <div className="space-y-4 p-4">
            <Field label="Passing at or above (%)"><Input type="number" min={0} max={100} disabled={readOnly} value={content.scoring_rules.passing_threshold} onChange={(e) => updateContent((c) => ({ ...c, scoring_rules: { ...c.scoring_rules, passing_threshold: Number(e.target.value) } }))} /></Field>
            <Field label="Warning at or above (%)" hint="Below this the store is failing."><Input type="number" min={0} max={100} disabled={readOnly} value={content.scoring_rules.warning_threshold} onChange={(e) => updateContent((c) => ({ ...c, scoring_rules: { ...c.scoring_rules, warning_threshold: Number(e.target.value) } }))} /></Field>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-0.5 size-4" disabled={readOnly} checked={content.scoring_rules.use_weighted_scoring} onChange={(e) => updateContent((c) => ({ ...c, scoring_rules: { ...c.scoring_rules, use_weighted_scoring: e.target.checked } }))} />
              <span><strong>Weighted scoring</strong><br /><span className="text-muted-fg">Each item counts by its points. Off: every item counts equally.</span></span>
            </label>
          </div>
        </Card>
      )}

      {tab === 'labor' && (
        <Card className="max-w-xl">
          <CardHeader title="Labor Planning Calculator" subtitle="Adds a Labor tab to the audit. It is informational and does not affect the score." />
          <div className="space-y-3 p-4 text-sm">
            {(['enabled', 'allow_comment', 'allow_photo', 'allow_video'] as const).map((k) => (
              <label key={k} className="flex items-center gap-2">
                <input type="checkbox" className="size-4" disabled={readOnly} checked={content.labor_config[k]} onChange={(e) => updateContent((c) => ({ ...c, labor_config: { ...c.labor_config, [k]: e.target.checked } }))} />
                {{ enabled: 'Include the labor tab', allow_comment: 'Allow notes', allow_photo: 'Allow photos', allow_video: 'Allow video' }[k]}
              </label>
            ))}
          </div>
        </Card>
      )}

      {tab === 'availability' && (
        <Card className="max-w-2xl">
          <CardHeader title="Where it’s used" />
          <div className="space-y-4 p-4">
            <Field label="Description"><Textarea disabled={readOnly} value={meta.description} onChange={(e) => updateMeta({ description: e.target.value })} /></Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Audit type"><Input disabled={readOnly} value={meta.audit_type} placeholder="Full Audit, Quick Check…" onChange={(e) => updateMeta({ audit_type: e.target.value })} /></Field>
              <Field label="Purpose"><Input disabled={readOnly} value={meta.operational_purpose} placeholder="Food Safety, Operations…" onChange={(e) => updateMeta({ operational_purpose: e.target.value })} /></Field>
            </div>
            <Field label="Available to">
              <Select disabled={readOnly} value={meta.visibility} onChange={(e) => updateMeta({ visibility: e.target.value as TemplateVisibility })}>
                <option value="all_stores">All stores</option>
                <option value="layouts">Stores with a given layout</option>
                <option value="store_specific">Specific stores</option>
              </Select>
            </Field>
            {meta.visibility === 'layouts' && (
              <div className="flex flex-wrap gap-3 text-sm">
                {STORE_LAYOUTS.map((l) => (
                  <label key={l} className="inline-flex items-center gap-1.5">
                    <input type="checkbox" className="size-4" disabled={readOnly} checked={meta.layouts.includes(l)} onChange={(e) => updateMeta({ layouts: e.target.checked ? [...meta.layouts, l] : meta.layouts.filter((x) => x !== l) })} />
                    {STORE_LAYOUT_LABELS[l]}
                  </label>
                ))}
              </div>
            )}
            {meta.visibility === 'store_specific' && (
              <div className="grid max-h-64 gap-1.5 overflow-y-auto rounded-lg border p-3 text-sm sm:grid-cols-2">
                {stores.data?.map((s) => (
                  <label key={s.id} className="inline-flex items-center gap-1.5">
                    <input type="checkbox" className="size-4" disabled={readOnly} checked={meta.store_ids.includes(s.id)} onChange={(e) => updateMeta({ store_ids: e.target.checked ? [...meta.store_ids, s.id] : meta.store_ids.filter((x) => x !== s.id) })} />
                    {s.name} <span className="text-muted-fg">#{s.store_number}</span>
                  </label>
                ))}
              </div>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="size-4" disabled={readOnly} checked={meta.is_default} onChange={(e) => updateMeta({ is_default: e.target.checked })} />
              Default template (listed first when starting an audit)
            </label>
          </div>
        </Card>
      )}

      {tab === 'history' && (
        <Card className="max-w-2xl">
          <CardHeader title="Published versions" subtitle="Audits always show the exact version they were run on." />
          {t.versions.length === 0 ? <p className="p-4 text-sm text-muted-fg">Not published yet.</p> : (
            <ul className="divide-y text-sm">
              {t.versions.map((v) => (
                <li key={v.id} className="flex items-start justify-between gap-3 px-4 py-2.5">
                  <div><Badge tone={v.id === t.current_version_id ? 'pass' : 'neutral'}>v{v.version_no}{v.id === t.current_version_id && ' · current'}</Badge>{v.change_note && <span className="ml-2">{v.change_note}</span>}</div>
                  <span className="text-muted-fg whitespace-nowrap">{fmtDateTime(v.published_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {publishOpen && <PublishDialog templateId={t.id} nextVersion={(t.current_version_no ?? 0) + 1} beforePublish={flushSave} onClose={() => setPublishOpen(false)} onDone={async () => { await refetch(); await qc.invalidateQueries({ queryKey: ['templates'] }); }} />}
    </>
  );
}

function PublishDialog({ templateId, nextVersion, beforePublish, onClose, onDone }: { templateId: string; nextVersion: number; beforePublish: () => Promise<void>; onClose: () => void; onDone: () => Promise<void> }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Publish version ${nextVersion}?`}
      description="New audits will use this version. Audits already started keep the version they began with."
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={async () => {
        setBusy(true);
        try {
          await beforePublish();
          await api.post(`/api/templates/${templateId}/publish`, { change_note: note });
          toast.success(`Published v${nextVersion}.`);
          await onDone();
          onClose();
        } catch (e) { setError(e); } finally { setBusy(false); }
      }}>Publish</Button></>}
    >
      <Field label="What changed?"><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Added carhop tray check" /></Field>
      <div className="mt-3"><ErrorNote error={error} /></div>
    </Dialog>
  );
}
